"use client";

import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { getFanClubViewerState, joinFanClub, leaveFanClub } from "@/lib/actions/fan-club-actions";
import type { FanClubPublicSummary, FanClubViewerState } from "@/lib/fan-clubs/types";
import { toPublicFanClubProps } from "@/components/fan-clubs/public-fan-club-props";
type FanClubMemberDetail = NonNullable<FanClubViewerState["fanClub"]>;
const VIEWER_STATE_ERROR = "We couldn't load your fan club membership. Please try again.";
const MEMBERSHIP_UPDATE_ERROR = "We couldn't update your membership. Please try again.";
export function useFanClubMembership({
  storyId,
  storySlug,
  initialFanClub
}: {
  storyId: string;
  storySlug: string;
  initialFanClub: FanClubPublicSummary;
}) {
  const router = useRouter();
  const [fanClub, setFanClub] = useState<FanClubPublicSummary | null>(initialFanClub);
  const [memberFanClub, setMemberFanClub] = useState<FanClubMemberDetail | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingViewer, setIsLoadingViewer] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const viewerRequestId = useRef(0);
  const [isPending, startTransition] = useTransition();
  const applyViewerState = useCallback((viewerState: FanClubViewerState) => {
    setFanClub(viewerState.fanClub ? toPublicFanClubProps(viewerState.fanClub) : null);
    setMemberFanClub(viewerState.isMember ? viewerState.fanClub : null);
    setIsMember(viewerState.isMember);
    setIsAuthenticated(viewerState.isAuthenticated);
  }, []);
  const loadViewerState = useCallback(async () => {
    const requestId = ++viewerRequestId.current;
    setIsLoadingViewer(true);
    setError(null);
    try {
      const result = await getFanClubViewerState(storyId);
      if (requestId !== viewerRequestId.current) return;
      if (result.data === null) {
        setError(result.error);
      } else {
        applyViewerState(result.data);
      }
    } catch {
      if (requestId === viewerRequestId.current) {
        setError(VIEWER_STATE_ERROR);
      }
    } finally {
      if (requestId === viewerRequestId.current) {
        setIsLoadingViewer(false);
      }
    }
  }, [applyViewerState, storyId]);
  useEffect(() => {
    void loadViewerState();
    return () => {
      viewerRequestId.current += 1;
    };
  }, [loadViewerState]);
  function toggleMembership() {
    if (!isAuthenticated) {
      const destination = encodeURIComponent(`/story/${storySlug}#fan-club`);
      router.push(`/login?redirect=${destination}`);
      return;
    }
    startTransition(async () => {
      setError(null);
      try {
        const result_0 = isMember ? await leaveFanClub(storyId) : await joinFanClub(storyId);
        if (result_0.data === null) {
          setError(result_0.error);
          return;
        }
        const nextIsMember = !isMember;
        const refreshed = await getFanClubViewerState(storyId);
        if (refreshed.data !== null) {
          applyViewerState(refreshed.data);
          return;
        }
        setError(refreshed.error);
        const memberDelta = isMember ? -1 : 1;
        setIsMember(nextIsMember);
        setMemberFanClub(null);
        setFanClub(current => current && {
          ...current,
          memberCount: Math.max(0, current.memberCount + memberDelta)
        });
      } catch {
        setError(MEMBERSHIP_UPDATE_ERROR);
      }
    });
  }
  return {
    fanClub,
    memberFanClub,
    isMember,
    isLoadingViewer,
    isPending,
    error,
    toggleMembership,
    loadViewerState
  };
}